import { useState, useEffect } from 'react'
import { useAuthStore } from '@/store/authStore'
import { db, storage } from '@/config/firebase'
import { doc, updateDoc, setDoc } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { QrCode, Save, AlertCircle, XCircle, CreditCard, Key, Eye, EyeOff, CheckCircle2 } from 'lucide-react'
import toast from 'react-hot-toast'

type PaymentProvider = 'manual' | 'mercadopago' | 'infinitypay'

export default function PaymentSettings() {
    const { currentOrg, setCurrentOrg } = useAuthStore()

    // Global Active Provider State
    const [activeProvider, setActiveProvider] = useState<PaymentProvider>(currentOrg?.paymentConfig?.provider || 'manual')

    // Configuration Tabs State (just for UI navigation)
    const [configTab, setConfigTab] = useState<PaymentProvider>(currentOrg?.paymentConfig?.provider || 'manual')

    const [loading, setLoading] = useState(false)

    // PIX State
    const [pixData, setPixData] = useState({
        key: currentOrg?.paymentConfig?.pixConfig?.key || '',
        instructions: currentOrg?.paymentConfig?.pixConfig?.instructions || 'Realize o pagamento e envie o comprovante para confirmação.',
    })
    const [qrFile, setQrFile] = useState<File | null>(null)
    const [qrPreview, setQrPreview] = useState<string | null>(currentOrg?.paymentConfig?.pixConfig?.qrCodeUrl || null)

    // Mercado Pago State
    const [mpData, setMpData] = useState({
        accessToken: '',
        publicKey: currentOrg?.paymentConfig?.mercadoPago?.publicKey || '',
        enabled: currentOrg?.paymentConfig?.mercadoPago?.enabled || false
    })
    const [showAccessToken, setShowAccessToken] = useState(false)

    // InfinityPay State
    const [ipData, setIpData] = useState({
        infiniteTag: currentOrg?.paymentConfig?.infinityPay?.infiniteTag || '',
        enabled: currentOrg?.paymentConfig?.infinityPay?.enabled || false
    })

    // Update local state when org changes
    useEffect(() => {
        if (currentOrg?.paymentConfig) {
            setActiveProvider(currentOrg.paymentConfig.provider || 'manual')
            // Don't auto-switch configTab to avoid jumping while editing if background updates happen
        }
    }, [currentOrg?.paymentConfig?.provider])

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            setQrFile(file)
            setQrPreview(URL.createObjectURL(file))
        }
    }

    const saveProviderSelection = async (provider: PaymentProvider) => {
        if (!currentOrg) return
        try {
            await updateDoc(doc(db, 'organizations', currentOrg.id), {
                'paymentConfig.provider': provider
            })
            // Optimistic update
            const updatedOrg = {
                ...currentOrg,
                paymentConfig: { ...currentOrg.paymentConfig, provider }
            }
            setCurrentOrg(updatedOrg as any)
            setActiveProvider(provider)
            toast.success(`Método de pagamento alterado para ${provider === 'manual' ? 'Pix Manual' : provider === 'mercadopago' ? 'Mercado Pago' : 'InfinityPay'}`)
        } catch (error) {
            toast.error('Erro ao alterar método de pagamento')
        }
    }

    const handleSavePix = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!currentOrg) return

        setLoading(true)
        try {
            let qrCodeUrl = currentOrg.paymentConfig?.pixConfig?.qrCodeUrl || ''

            if (qrFile) {
                const storageRef = ref(storage, `organizations/${currentOrg.id}/pix/qrcode_${Date.now()}`)
                const snapshot = await uploadBytes(storageRef, qrFile)
                qrCodeUrl = await getDownloadURL(snapshot.ref)
            }

            const updatedPixConfig = {
                ...pixData,
                qrCodeUrl,
            }

            // DESTRUCTIVE FIX: Preserve existing config using dot notation or merge
            await updateDoc(doc(db, 'organizations', currentOrg.id), {
                'paymentConfig.pixConfig': updatedPixConfig,
                // If the user is saving Pix config, they might want to switch to it, but let's stick to the selector for strictness
                // or we can auto-switch if they are on the tab. 
                // Let's NOT auto-switch provider here to respect the "Global Selector" logic strictly.
            })

            setCurrentOrg({
                ...currentOrg,
                paymentConfig: {
                    ...currentOrg.paymentConfig,
                    pixConfig: updatedPixConfig
                }
            } as any)

            toast.success('Configurações de PIX salvas!')
        } catch (error: any) {
            console.error('Erro ao salvar PIX:', error)
            toast.error('Erro ao salvar configurações.')
        } finally {
            setLoading(false)
        }
    }

    const handleSaveMercadoPago = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!currentOrg) return

        if (!mpData.accessToken || !mpData.publicKey) {
            toast.error('Access Token e Public Key são obrigatórios')
            return
        }

        setLoading(true)
        try {
            await setDoc(doc(db, 'organizations', currentOrg.id, 'secrets', 'mercadopago'), {
                accessToken: mpData.accessToken,
                updatedAt: new Date()
            })

            const updatedMpConfig = {
                publicKey: mpData.publicKey,
                enabled: mpData.enabled
            }

            await updateDoc(doc(db, 'organizations', currentOrg.id), {
                'paymentConfig.mercadoPago': updatedMpConfig
            })

            setCurrentOrg({
                ...currentOrg,
                paymentConfig: {
                    ...currentOrg.paymentConfig,
                    mercadoPago: updatedMpConfig
                }
            } as any)

            toast.success('Configurações do Mercado Pago salvas!')
            setMpData({ ...mpData, accessToken: '' })
        } catch (error: any) {
            toast.error('Erro ao salvar configurações.')
        } finally {
            setLoading(false)
        }
    }

    // New Effect: Check if secret exists (visual feedback)
    useEffect(() => {
        const checkSecret = async () => {
            if (!currentOrg) return
            try {
                // We try to read the document. If rules allow reading (which we enabled), we can see if it has data.
                // Security note: In strict prod, we might only allow 'list' or have a 'hasSecret' field in public doc.
                // For now, we engage readable secrets conformant to new rules.
                const secretDoc = await import('firebase/firestore').then(m => m.getDoc(doc(db, 'organizations', currentOrg.id, 'secrets', 'mercadopago')))
                if (secretDoc.exists() && secretDoc.data().accessToken) {
                    setMpData(prev => ({ ...prev, accessToken: '••••••••••••••••••••••••' }))
                }
            } catch (e) {
                // Ignore permission errors if any
                console.log('Could not check secret status', e)
            }
        }
        checkSecret()
    }, [currentOrg?.id])

    const handleSaveInfinityPay = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!currentOrg) return

        if (!ipData.infiniteTag) {
            toast.error('InfiniteTag é obrigatório')
            return
        }

        setLoading(true)
        try {
            const updatedIpConfig = {
                infiniteTag: ipData.infiniteTag,
                enabled: ipData.enabled
            }

            await updateDoc(doc(db, 'organizations', currentOrg.id), {
                'paymentConfig.infinityPay': updatedIpConfig
            })

            setCurrentOrg({
                ...currentOrg,
                paymentConfig: {
                    ...currentOrg.paymentConfig,
                    infinityPay: updatedIpConfig
                }
            } as any)

            toast.success('Configurações da InfinitePay salvas!')
        } catch (error: any) {
            toast.error('Erro ao salvar configurações.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="max-w-4xl mx-auto space-y-8">

            {/* Global Provider Selector */}
            <div className="bg-white p-8 rounded-[2rem] border border-[#001c28]/10 shadow-xl shadow-[#001c28]/5 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#001c28] via-[#d4a94d] to-[#001c28]"></div>

                <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-[#001c28] flex items-center justify-center text-[#d4a94d]">
                        <CreditCard className="w-6 h-6" />
                    </div>
                    <div>
                        <h2 className="text-xl font-black text-[#001c28] uppercase italic tracking-tighter">Método de Recebimento Oficial</h2>
                        <p className="text-xs text-[#001c28]/60 font-bold">Escolha qual sistema processará os pagamentos da organização.</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                        { id: 'manual', label: 'Pix Manual', icon: QrCode },
                        { id: 'mercadopago', label: 'Mercado Pago', icon: CreditCard },
                        { id: 'infinitypay', label: 'InfinityPay', icon: CreditCard },
                    ].map((provider) => {
                        const isSelected = activeProvider === provider.id
                        const Icon = provider.icon
                        return (
                            <button
                                key={provider.id}
                                onClick={() => saveProviderSelection(provider.id as PaymentProvider)}
                                className={`relative p-4 rounded-xl border-2 transition-all duration-300 flex flex-col items-center justify-center gap-3 group ${isSelected
                                    ? 'border-[#d4a94d] bg-[#001c28] text-white shadow-lg scale-[1.02]'
                                    : 'border-gray-100 bg-gray-50 text-gray-500 hover:border-gray-200 hover:bg-white'
                                    }`}
                            >
                                {isSelected && (
                                    <div className="absolute top-3 right-3 text-[#d4a94d]">
                                        <CheckCircle2 className="w-5 h-5 fill-current" />
                                    </div>
                                )}
                                <Icon className={`w-8 h-8 ${isSelected ? 'text-[#d4a94d]' : 'text-gray-400 group-hover:text-gray-600'}`} />
                                <span className="font-black uppercase tracking-widest text-xs">{provider.label}</span>
                            </button>
                        )
                    })}
                </div>
            </div>

            {/* Configuration Tabs */}
            <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 overflow-hidden">
                <div className="bg-gray-50 p-4 border-b border-gray-100">
                    <h3 className="text-sm font-black text-gray-400 uppercase tracking-widest ml-4">Configurar Integrações</h3>
                </div>

                <div className="flex border-b border-gray-100 overflow-x-auto">
                    <button
                        onClick={() => setConfigTab('manual')}
                        className={`flex-1 min-w-[120px] px-6 py-4 font-bold text-xs uppercase tracking-wider transition-all ${configTab === 'manual'
                            ? 'bg-white text-[#001c28] border-b-2 border-[#001c28]'
                            : 'bg-gray-50 text-gray-400 hover:text-gray-600'
                            }`}
                    >
                        Pix Manual
                    </button>
                    <button
                        onClick={() => setConfigTab('mercadopago')}
                        className={`flex-1 min-w-[120px] px-6 py-4 font-bold text-xs uppercase tracking-wider transition-all ${configTab === 'mercadopago'
                            ? 'bg-white text-[#001c28] border-b-2 border-[#001c28]'
                            : 'bg-gray-50 text-gray-400 hover:text-gray-600'
                            }`}
                    >
                        Mercado Pago
                    </button>
                    <button
                        onClick={() => setConfigTab('infinitypay')}
                        className={`flex-1 min-w-[120px] px-6 py-4 font-bold text-xs uppercase tracking-wider transition-all ${configTab === 'infinitypay'
                            ? 'bg-white text-[#001c28] border-b-2 border-[#001c28]'
                            : 'bg-gray-50 text-gray-400 hover:text-gray-600'
                            }`}
                    >
                        InfinityPay
                    </button>
                </div>

                {/* PIX Manual Config */}
                {configTab === 'manual' && (
                    <form onSubmit={handleSavePix} className="p-8">
                        {/* ... Existing Pix Form Content ... */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Chave PIX</label>
                                    <input
                                        type="text"
                                        className="input input-primary w-full"
                                        placeholder="Ex: financeiro@empresa.com"
                                        required
                                        value={pixData.key}
                                        onChange={(e) => setPixData({ ...pixData, key: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Instruções</label>
                                    <textarea
                                        className="input input-primary w-full h-32 py-3 resize-none"
                                        placeholder="Instruções para o usuário..."
                                        value={pixData.instructions}
                                        onChange={(e) => setPixData({ ...pixData, instructions: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="space-y-4">
                                <label className="block text-sm font-bold text-gray-700 mb-2">QR Code (Opcional)</label>
                                <div className="relative">
                                    <div className={`aspect-square rounded-2xl border-2 border-dashed flex items-center justify-center ${qrPreview ? 'border-primary-200 bg-primary-50/10' : 'border-gray-200 bg-gray-50'}`}>
                                        {qrPreview ? (
                                            <img src={qrPreview} alt="QR Code" className="w-4/5 h-4/5 object-contain" />
                                        ) : (
                                            <QrCode className="w-12 h-12 text-gray-300" />
                                        )}
                                        <label className="absolute inset-0 cursor-pointer">
                                            <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                                        </label>
                                    </div>
                                    {qrPreview && (
                                        <button type="button" onClick={() => { setQrFile(null); setQrPreview(null); }} className="absolute -top-2 -right-2 bg-red-500 text-white p-1.5 rounded-full"><XCircle className="w-4 h-4" /></button>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end pt-6 mt-6 border-t border-gray-100">
                            <button type="submit" disabled={loading} className="btn-primary flex items-center gap-2 px-6 py-3"><Save className="w-5 h-5" /> {loading ? 'Salvando...' : 'Salvar Configuração Pix'}</button>
                        </div>
                    </form>
                )}

                {/* Mercado Pago Config */}
                {configTab === 'mercadopago' && (
                    <form onSubmit={handleSaveMercadoPago} className="p-8 space-y-6">
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-900">
                                <p className="font-bold mb-1">Como obter as credenciais:</p>
                                <ol className="list-decimal list-inside space-y-1 text-xs">
                                    <li>Acesse <a href="https://www.mercadopago.com.br/developers" target="_blank" rel="noopener noreferrer" className="underline">Mercado Pago Developers</a></li>
                                    <li>Vá em "Suas integrações" → "Criar aplicação"</li>
                                    <li>Copie o <strong>Access Token</strong> e a <strong>Public Key</strong></li>
                                </ol>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2"><Key className="w-4 h-4" /> Access Token (Privado)</label>
                                <div className="relative">
                                    <input type={showAccessToken ? 'text' : 'password'} className="input input-primary w-full pr-12" placeholder="APP_USR-..." required value={mpData.accessToken} onChange={(e) => setMpData({ ...mpData, accessToken: e.target.value })} />
                                    <button type="button" onClick={() => setShowAccessToken(!showAccessToken)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">{showAccessToken ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Public Key</label>
                                <input type="text" className="input input-primary w-full" placeholder="APP_USR-..." required value={mpData.publicKey} onChange={(e) => setMpData({ ...mpData, publicKey: e.target.value })} />
                            </div>

                            <div className="flex items-center gap-3 pt-2">
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={mpData.enabled}
                                        onChange={(e) => setMpData({ ...mpData, enabled: e.target.checked })}
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#d4a94d]"></div>
                                </label>
                                <span className="text-sm font-bold text-gray-700">Ativar Integração (Permitir pagamentos)</span>
                            </div>
                        </div>
                        <div className="flex justify-end pt-6 mt-6 border-t border-gray-100">
                            <button type="submit" disabled={loading} className="btn-primary flex items-center gap-2 px-6 py-3"><Save className="w-5 h-5" /> {loading ? 'Salvando...' : 'Salvar Configuração MP'}</button>
                        </div>
                    </form>
                )}

                {/* InfinityPay Config */}
                {configTab === 'infinitypay' && (
                    <form onSubmit={handleSaveInfinityPay} className="p-8 space-y-6">
                        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-purple-900">
                                <p className="font-bold mb-1">Como encontrar seu InfiniteTag:</p>
                                <ol className="list-decimal list-inside space-y-1 text-xs">
                                    <li>Abra o app InfinitePay</li>
                                    <li>Seu InfiniteTag aparece como <strong>$seunome</strong> no topo</li>
                                    <li>Cole aqui com ou sem o símbolo $</li>
                                </ol>
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2"><Key className="w-4 h-4 inline mr-2" /> InfiniteTag</label>
                            <input type="text" className="input input-primary w-full" placeholder="Ex: $rbconsul ou rbconsul" required value={ipData.infiniteTag} onChange={(e) => setIpData({ ...ipData, infiniteTag: e.target.value })} />
                        </div>
                        <div className="flex justify-end pt-6 mt-6 border-t border-gray-100">
                            <button type="submit" disabled={loading} className="btn-primary flex items-center gap-2 px-6 py-3"><Save className="w-5 h-5" /> {loading ? 'Salvando...' : 'Salvar Configuração InfinityPay'}</button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    )
}
